import React, { useEffect, useRef, useState } from 'react';
import { ProTable } from '@ant-design/pro-components';
import { Button, Card, Tag, Space, Modal, message, Select, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ProColumns, ActionType } from '@ant-design/pro-components';
import { getFoodItems, deleteFoodItem, batchUpdateFoodItemStatus, getCategoryTree, FoodItem, FoodCategory } from '../../api/foods';

interface FlatCategory {
  id: string;
  name: string;
}

const flattenCategories = (cats: FoodCategory[]): FlatCategory[] =>
  cats.reduce<FlatCategory[]>((acc, c) => {
    acc.push({ id: c.id, name: c.name });
    if (c.children) acc.push(...flattenCategories(c.children));
    return acc;
  }, []);

const Items: React.FC = () => {
  const actionRef = useRef<ActionType>();
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [flatCategories, setFlatCategories] = useState<FlatCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    getCategoryTree().then((data) => {
      setFlatCategories(flattenCategories(data));
    });
  }, []);

  const columns: ProColumns<FoodItem>[] = [
    { title: '名称', dataIndex: 'name', ellipsis: true, width: 140 },
    {
      title: '所属分类', key: 'categoryName', width: 110,
      render: (_, r) => {
        const cat = flatCategories.find((c) => c.id === r.categoryId);
        return cat ? cat.name : '-';
      },
    },
    { title: '别名', dataIndex: 'aliases', render: (_, record) => record.aliases?.join(', ') || '-', ellipsis: true },
    { title: '热量(/100g)', dataIndex: 'caloriesPer100g', renderText: (v) => v != null ? `${v}kcal` : '-', width: 120 },
    { title: '蛋白质(/100g)', dataIndex: 'proteinPer100g', renderText: (v) => v != null ? `${v}g` : '-', width: 120 },
    { title: '脂肪(/100g)', dataIndex: 'fatPer100g', renderText: (v) => v != null ? `${v}g` : '-', width: 110 },
    { title: '碳水(/100g)', dataIndex: 'carbsPer100g', renderText: (v) => v != null ? `${v}g` : '-', width: 110 },
    {
      title: '启用', dataIndex: 'isEnabled', width: 70,
      render: (_, record) =>
        record.isEnabled ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    { title: '来源', dataIndex: 'source', ellipsis: true, width: 100 },
    {
      title: '操作', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => navigate(`/foods/items/${record.id}`)}>编辑</Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  const handleDelete = (item: FoodItem) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除食物 "${item.name}" 吗？删除后将不再显示在列表中。`,
      okText: '确认', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          await deleteFoodItem(item.id);
          message.success('删除成功');
          actionRef.current?.reload();
        } catch { /* handled */ }
      },
    });
  };

  const handleBatchStatus = (isEnabled: boolean) => {
    if (selectedRowKeys.length === 0) { message.warning('请先选择食物'); return; }
    const action = isEnabled ? '启用' : '禁用';
    Modal.confirm({
      title: `批量${action}`,
      content: `确定要${action}选中的 ${selectedRowKeys.length} 个食物吗？`,
      okText: '确认', cancelText: '取消',
      onOk: async () => {
        try {
          await batchUpdateFoodItemStatus(selectedRowKeys as string[], isEnabled);
          message.success(`批量${action}成功`);
          setSelectedRowKeys([]);
          actionRef.current?.reload();
        } catch { /* handled */ }
      },
    });
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Card size="small" style={{ marginBottom: 16, flexShrink: 0 }}>
        <Space wrap>
          <span style={{ fontWeight: 500, fontSize: 13 }}>分类筛选：</span>
          <Select
            mode="multiple"
            placeholder="选择分类（可多选）"
            allowClear
            showSearch
            value={selectedCategoryIds}
            onChange={(values) => {
              setSelectedCategoryIds(values);
              actionRef.current?.reload();
            }}
            style={{ minWidth: 280 }}
            options={flatCategories.map((c) => ({ label: c.name, value: c.id }))}
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Input.Search
            placeholder="搜索食物名称..."
            allowClear
            style={{ width: 220 }}
            onSearch={(val) => {
              setKeyword(val);
              actionRef.current?.reload();
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setKeyword('');
                actionRef.current?.reload();
              }
            }}
          />
        </Space>
      </Card>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <ProTable<FoodItem>
          actionRef={actionRef}
          columns={columns}
          params={{ categoryIds: selectedCategoryIds.join(','), keyword }}
          request={async (params) => {
            const res = await getFoodItems({
              categoryIds: params.categoryIds as string || undefined,
              keyword: params.keyword as string || undefined,
              page: params.current,
              pageSize: params.pageSize,
            });
            return { data: res.items, success: true, total: res.total };
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          rowKey="id"
          search={false}
          options={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          toolBarRender={() => [
            <Space key="toolbar">
              {selectedRowKeys.length > 0 && (
                <>
                  <span style={{ marginRight: 8 }}>已选 {selectedRowKeys.length} 项</span>
                  <Button size="small" onClick={() => handleBatchStatus(true)}>批量启用</Button>
                  <Button size="small" danger onClick={() => handleBatchStatus(false)}>批量禁用</Button>
                </>
              )}
              <Button type="primary" key="add" onClick={() => navigate('/foods/items/new')}>
                添加食物
              </Button>
            </Space>,
          ]}
        />
      </div>
    </div>
  );
};

export default Items;
